import { useState, useMemo } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useListadoFidelizacion } from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { DynamicDataTable, type RowAction } from './DynamicDataTable'
import {
  DynamicFiltersBar,
  type DynamicFiltersValue,
  type FieldOption,
} from './DynamicFiltersBar'
import type { FiltrosListadoBase, MetadataResponse, RecursoListado } from '../types'

interface ListadoTabProps {
  configId: number
  recurso: RecursoListado
  metadata?: MetadataResponse | null
  /** Recursos sin filtros dinámicos / sin filtro de fechas (ej: catálogo de productos). */
  hideDateFilters?: boolean
  /** Oculta sólo Desde/Hasta en la barra de filtros, mantiene el resto. */
  hideDateRange?: boolean
  omitDynamicFields?: string[]
  /** Acción por fila (botón). */
  rowAction?: RowAction
  /** Rango fijo (sobrescribe los filtros desde/hasta — útil con MonthFilter externo). */
  rango?: { desde?: string; hasta?: string }
  /** Mezcla campos extra en cada fila antes de pasarlos al renderer (ej. resolver IDs a nombres). */
  enrichRow?: (row: Record<string, unknown>) => Record<string, unknown>
  /** Filtro client-side aplicado tras el fetch (ej. limitar a ciertos estados). */
  rowFilter?: (row: Record<string, unknown>) => boolean
  /** Opciones para renderizar dropdowns en lugar de inputs de texto en campos string filtrables. */
  fieldOptions?: Record<string, string[] | FieldOption[]>
  /** Filtros controlados desde el padre (modo controlado). Si se omite, ListadoTab maneja su propio estado. */
  filtros?: DynamicFiltersValue
  onFiltrosChange?: (next: DynamicFiltersValue) => void
  /** Oculta la barra de filtros (cuando el padre la renderiza externamente). */
  hideFiltersBar?: boolean
  /** Keys de `campos_externos` a NO renderizar como columna en la tabla. */
  omitCampos?: string[]
  /** Selects de cascada extra (ej. Jefe de Ventas / Representante Médico); ver DynamicFiltersBar. */
  cascadeSelects?: Array<{
    key: string
    label: string
    options: FieldOption[]
    value?: string
    onChange: (v: string | undefined) => void
  }>
}

export function ListadoTab({
  configId,
  recurso,
  metadata,
  hideDateFilters,
  hideDateRange,
  omitDynamicFields,
  rowAction,
  rango,
  enrichRow,
  rowFilter,
  fieldOptions,
  filtros: filtrosProp,
  onFiltrosChange,
  hideFiltersBar,
  omitCampos,
  cascadeSelects,
}: ListadoTabProps) {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [filtrosInternal, setFiltrosInternal] = useState<DynamicFiltersValue>({})
  const isControlled = filtrosProp !== undefined
  const filtros = isControlled ? filtrosProp : filtrosInternal
  const setFiltros = (next: DynamicFiltersValue) => {
    if (isControlled) onFiltrosChange?.(next)
    else setFiltrosInternal(next)
  }

  const queryParams = useMemo<FiltrosListadoBase>(() => {
    const base: FiltrosListadoBase = { page, page_size: pageSize, ...filtros }
    if (rango?.desde) base.desde = rango.desde
    if (rango?.hasta) base.hasta = rango.hasta
    return base
  }, [page, pageSize, filtros, rango])

  const { data, isLoading, isFetching, error } = useListadoFidelizacion(
    configId,
    recurso,
    queryParams,
  )

  const enrichedData = useMemo(() => {
    if (!data) return data
    const enriched = enrichRow ? data.items.map((r) => ({ ...r, ...enrichRow(r) })) : data.items
    const filtered = rowFilter ? enriched.filter(rowFilter) : enriched
    if (filtered === data.items) return data
    return { ...data, items: filtered }
  }, [data, enrichRow, rowFilter])

  const handleFiltrosChange = (next: DynamicFiltersValue) => {
    setFiltros(next)
    setPage(1) // reset al cambiar filtros
  }

  return (
    <div className="space-y-4">
      {!hideDateFilters && !hideFiltersBar && (
        <DynamicFiltersBar
          configId={configId}
          metadata={metadata}
          value={filtros}
          onChange={handleFiltrosChange}
          omitDynamicFields={omitDynamicFields}
          hideDateRange={hideDateRange}
          fieldOptions={fieldOptions}
          cascadeSelects={cascadeSelects}
        />
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(error).message}</AlertDescription>
        </Alert>
      )}

      <DynamicDataTable
        configId={configId}
        recurso={recurso}
        metadata={metadata}
        data={enrichedData}
        isLoading={isLoading}
        isFetching={isFetching}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        rowAction={rowAction}
        omitCampos={omitCampos}
      />
    </div>
  )
}
